import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Loader2, X, Check, Volume2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Voice Input Button Component
 * Provides a microphone button that captures voice input and converts to text
 */
const VoiceInputButton = ({ 
  onResult, 
  onPartialResult,
  language = 'es-ES',
  className = '',
  size = 'default',
  showTranscript = true,
  disabled = false
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Check if Web Speech API is supported
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = language;
      recognition.maxAlternatives = 1;
      
      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const text = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += text;
          } else {
            interimTranscript += text;
          }
        }
        
        const currentTranscript = interimTranscript || finalTranscript;
        setTranscript(currentTranscript);
        
        if (interimTranscript) {
          onPartialResult?.(interimTranscript);
        }
        
        if (finalTranscript) {
          // Show confirmation before applying
          setShowConfirmation(true);
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setError(event.error);
        setIsListening(false);
        
        // Auto-clear error after 3 seconds
        setTimeout(() => setError(null), 3000);
      };
      
      recognition.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current = recognition;
    } else {
      setIsSupported(false);
    }
    
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {}
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [language]);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening && !disabled) {
      setError(null);
      setTranscript('');
      setShowConfirmation(false);
      
      try {
        recognitionRef.current.start();
        setIsListening(true);
        
        // Auto-stop after 10 seconds
        timeoutRef.current = setTimeout(() => {
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        }, 10000);
      } catch (err) {
        console.error('Failed to start recognition:', err);
        setError('start-failed');
      }
    }
  }, [isListening, disabled]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isListening]);

  const handleConfirm = () => {
    if (transcript) {
      onResult?.(transcript);
    }
    setShowConfirmation(false);
    setTranscript('');
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setTranscript('');
  };

  const handleClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  const sizeClasses = {
    sm: 'w-8 h-8',
    default: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Mic Button */}
      <motion.button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`
          ${sizeClasses[size]} 
          rounded-full flex items-center justify-center transition-all
          ${isListening 
            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 animate-pulse' 
            : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:shadow-lg hover:shadow-purple-500/30'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        title={isListening ? 'Detener grabación' : 'Dictar con voz'}
      >
        {isListening ? (
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
          >
            <MicOff className={iconSizes[size]} />
          </motion.div>
        ) : (
          <Mic className={iconSizes[size]} />
        )}
      </motion.button>

      {/* Listening Indicator */}
      <AnimatePresence>
        {isListening && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50"
          >
            <div className="bg-navy-900 text-white px-4 py-2 rounded-xl shadow-lg whitespace-nowrap">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <motion.div 
                    animate={{ height: [4, 16, 4] }}
                    transition={{ repeat: Infinity, duration: 0.5, delay: 0 }}
                    className="w-1 bg-red-400 rounded-full"
                  />
                  <motion.div 
                    animate={{ height: [4, 16, 4] }}
                    transition={{ repeat: Infinity, duration: 0.5, delay: 0.1 }}
                    className="w-1 bg-red-400 rounded-full"
                  />
                  <motion.div 
                    animate={{ height: [4, 16, 4] }}
                    transition={{ repeat: Infinity, duration: 0.5, delay: 0.2 }}
                    className="w-1 bg-red-400 rounded-full"
                  />
                </div>
                <span className="text-sm">Escuchando...</span>
              </div>
              {transcript && showTranscript && (
                <p className="mt-1 text-xs text-navy-300 max-w-48 truncate">
                  "{transcript}"
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {showConfirmation && transcript && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            className="absolute top-full right-0 mt-2 z-50 w-72"
          >
            <div className="bg-white border border-navy-200 rounded-xl shadow-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Volume2 className="w-4 h-4 text-violet-600" />
                <span className="text-sm font-medium text-navy-900">Transcripción:</span>
              </div>
              <p className="text-sm text-navy-700 bg-navy-50 p-3 rounded-lg mb-3">
                "{transcript}"
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Usar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Indicator */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50"
          >
            <div className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs whitespace-nowrap">
              {error === 'no-speech' && 'No se detectó voz'}
              {error === 'audio-capture' && 'Error de micrófono'}
              {error === 'not-allowed' && 'Permiso denegado'}
              {error === 'start-failed' && 'Error al iniciar'}
              {!['no-speech', 'audio-capture', 'not-allowed', 'start-failed'].includes(error) && 'Error de voz'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceInputButton;
